import {
  CdkDropList,
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgClass, AsyncPipe } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl,
  FormControl,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogTitle,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
  MatError,
  MatFormField,
  MatInput,
  MatLabel,
} from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import {
  Observable,
  startWith,
  switchMap,
  map,
  from,
  shareReplay,
  firstValueFrom,
  of,
} from 'rxjs';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { ObjectConsumerMetadataComponent } from '../../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import {
  ConsumerEntityService,
  ConsumerEntityState,
} from '../../../consumer/services/consumer.entity.service';
import {
  normalizeOneRelation,
  normalizeManyRelations,
} from '../../../core/decorators/relation.decorator';
import { EntityStateModel } from '../../../core/state/entity.state';
import { Approval } from '../../../core/types';
import { ExamEntity } from '../../../lesson/exam/exam.entity';
import { ExamEntityService } from '../../../lesson/exam/services/exam.entity.service';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../../subject/subject.entity';
import { MultiComboEntitySearchComponent } from '../../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import {
  StateFilterCBComponent,
  getStatusColor,
  getStatusText,
} from '../../../utils/component/state-filter-combobox/state-cb.component';
import {
  SequenceBlock,
  SequenceList,
} from '../../interface/sequence-interface';
import { SequenceEntity } from '../../sequence.entity';

@Component({
  selector: 'pixl-exam-sequence-edit',
  imports: [
    MatDialogTitle,
    FormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatOption,
    MatSelect,
    MatDividerModule,
    MultiComboEntitySearchComponent,
    CdkDropList,
    DragDropModule,
    MatIcon,
    NgClass,
    StateFilterCBComponent,
    AsyncPipe,
    TranslocoModule,
    MatTabGroup,
    MatTab,
    ObjectConsumerMetadataComponent,
  ],
  templateUrl: './exam-sequence-edit.component.html',
  styleUrl: './exam-sequence-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExamSequenceEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<ExamSequenceEditComponent>>(MatDialogRef);

  data? = inject<Partial<SequenceEntity>>(MAT_DIALOG_DATA);

  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  sequences$!: Observable<SequenceEntity[]>;
  selectedConsumerName$!: Observable<string>;

  exams$!: Observable<ExamEntity[]>;

  allSelectedExams: ExamEntity[] = [];
  splitSelectedExams: ExamEntity[][] = [];

  currentPage = 1;
  pageSize = 20;
  examsSearchQuery: string | null = null;

  private store = inject(Store);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private examService = inject(ExamEntityService);
  private destroyRef = inject(DestroyRef);
  private examCache = new Map<string, ExamEntity>();

  private cdr = inject(ChangeDetectorRef);
  public searchResultObservables: Record<number, Observable<ExamEntity[]>> = {};

  private thisExams: SequenceBlock[] = [];

  public dataSequceneList: SequenceBlock[][] = [];

  public formGroup: FormGroup;

  get sequenceList(): SequenceBlock[][] {
    return (this.formGroup.get('sequence')?.value as SequenceList)?.list || [];
  }

  constructor() {
    const fb = this.fb;
    const data = this.data;

    const initConsumerData: Record<string, unknown> = {};
    const cd = data?.consumerData || {};
    for (const key of Object.keys(cd)) {
      initConsumerData[key] = [cd[key]];
    }

    this.formGroup = this.fb.group({
      name: [data?.name, Validators.required],
      consumerId: data?.consumerId || null,
      subjectId: data?.subjectId || null,
      approval: data?.approval || '',
      sequence: fb.control<SequenceList>(data?.sequence || { list: [] }),
      kind: data?.kind,
      consumerData: fb.group(initConsumerData),
      copyRestricted: data?.copyRestricted || null,
      active: data?.active || false,
      changedBy: data?.changedBy || null,
      version: data?.version || 1,
      sortName: data?.sortName || data?.name?.toLowerCase() || null,
      consumers: [
        normalizeOneRelation<ConsumerEntity>(data?.consumers) ||
        data?.consumerId ||
        null,
      ],
      subjects: [normalizeOneRelation<SubjectEntity>(data?.subjects)],
      exams: [normalizeManyRelations(data?.exams)],
    });

    // clean out empty sequence blocks in the middle
    const rawList = this.data?.sequence?.list;
    if (Array.isArray(rawList)) {
      const cleaned = rawList.filter(
        (block) => Array.isArray(block) && block.length > 0,
      );
      if (cleaned.length === 0) cleaned.push([]);

      this.formGroup.patchValue({
        sequence: { list: cleaned },
      });
    }
  }

  ngOnInit() {
    this.loadRelationData();
    this.initSearchObservables();

    this.selectedConsumerName$ = this.formGroup
      .get('consumers')!
      .valueChanges.pipe(
        startWith(this.formGroup.get('consumers')!.value),
        switchMap((id) =>
          this.store.select(ConsumerEntityState.byId).pipe(
            map((fn) => fn(id)),
            map((c) => c?.name || 'Konsument'),
          ),
        ),
      );
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery),
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery)),
      ),
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery)),
      ),
    );
  }

  private async initSearchObservables() {
    const idsToLoad = normalizeManyRelations(this.data?.exams) as string[];

    const promises = [];

    if (idsToLoad.length > 0) {
      const idQuery = { 'filter[id]': idsToLoad.join(',') };
      promises.push(this.examService.ensureLoaded(idQuery));
    }

    const pageQuery = {
      'page[size]': this.pageSize,
      'page[number]': this.currentPage,
      'sort': 'name',
    };
    promises.push(this.examService.ensureLoaded(pageQuery));

    await Promise.all(promises);

    this.getAllSelectedExamsFromState();
    this.getSplitSelectedExamsFromState();

    const sequenceValue = this.formGroup.get('sequence')?.value as
      | SequenceList |
      undefined;
    const currentSequenceList = sequenceValue?.list || [];
    const baseSearch$ = this.store
      .select(this.examService.allByQuery(pageQuery))
      .pipe(
        map((paginated) => {
          const combined: ExamEntity[] = [...this.allSelectedExams];

          const existingIds = new Set(this.allSelectedExams.map((t) => t.id));

          paginated.forEach((p) => {
            if (!existingIds.has(p.id)) {
              combined.push(p);
              existingIds.add(p.id);
            }
          });

          return combined;
        }),
        shareReplay(1),
      );

    for (let i = 0; i < currentSequenceList.length; i++) {
      this.searchResultObservables[i] = baseSearch$;
    }

    const examIds = this.allSelectedExams.map((t) => t.id);
    this.formGroup.get('exams')?.setValue(examIds);

    this.cdr.markForCheck();
  }

  private getAllSelectedExamsFromState(): void {
    const allSelectedExamIds = normalizeManyRelations(
      this.data?.exams,
    ) as string[];

    // Resolve full entities from store
    const state = this.store.selectSnapshot(
      this.examService.stateClass,
    ) as EntityStateModel<ExamEntity>;
    this.allSelectedExams = allSelectedExamIds
      .map((id) => state.entities[id])
      .filter((t): t is ExamEntity => !!t);

    this.rebuildExamCache();
  }

  private rebuildExamCache(): void {
    this.allSelectedExams.forEach((exam) => {
      this.examCache.set(exam.id, exam);
    });
  }

  private getSplitSelectedExamsFromState(): void {
    const listValue = this.formGroup.get('sequence')?.value as
      | SequenceList |
      undefined;
    const currentList = listValue?.list || [];

    const splitSelectedExamIds = currentList.map((row) =>
      row.map((item) => item.id),
    );

    // Resolve full entities from store
    const state = this.store.selectSnapshot(
      this.examService.stateClass,
    ) as EntityStateModel<ExamEntity>;
    this.splitSelectedExams = splitSelectedExamIds.map((row) =>
      row.map((id) => state.entities[id]).filter((e): e is ExamEntity => !!e),
    );
  }

  parseSequenceListData(): void {
    const sequenceValue = this.formGroup.get('sequence')?.value as
      | SequenceList |
      undefined;
    if (!sequenceValue?.list) return;

    this.dataSequceneList = [];

    sequenceValue.list.forEach((entry: SequenceBlock[], index: number) => {
      this.dataSequceneList[index] = entry.map((exam: SequenceBlock) => ({
        id: exam.id,
        name: this.getExamName(exam.id),
        type: exam.type,
        kind: exam.kind,
        approval: this.getExamApproval(exam.id),
        subKind: exam.subKind,
      }));
    });
  }

  parseEntityToBlock(entity: ExamEntity): SequenceBlock {
    return {
      id: entity?.id || '',
      // kind and type mixed up in the data. This is either exam or exercise
      type: this.data?.kind || '',
      // this kind is one of > 20 different kinds for the exam itself
      kind: entity.kind || '',
      subKind: '',
    };
  }

  public checkForErrors(formControl: AbstractControl): string {
    if (formControl.hasError('required')) {
      return 'Eingabe erforderlich';
    }

    return '';
  }

  receiveStateChange(event: ExamEntity[] | null, blockIndex: number): void {
    if (!event) return;

    const currentOptions: ExamEntity[] = [];
    this.searchResultObservables[blockIndex]
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values) =>
        values.forEach((value) => currentOptions.push(value)),
      );

    const normalizedSelection: ExamEntity[] = event.map((newExam) => {
      const existing = currentOptions.find((opt) => opt.id === newExam.id);
      if (existing) return existing;
      /**
       * At this place there was a line that also put the newly selected exam into the observable
       * used by the dropdown so all currently seleced exams would be shown at the top.
       * This however is currently not possible with it beeing an observable and no longer a
       * BahaviourSubject. So newly selected Exams wont be shown, the old ones will. Its the
       * best that can be done by now as selectiong exams for a sequence is going to change soon either way.
       */
      return newExam;
    });
    const blockList: SequenceBlock[] = normalizedSelection.map((value) =>
      this.parseEntityToBlock(value),
    );

    const sequenceControl = this.formGroup.get(
      'sequence',
    ) as FormControl<SequenceList>;
    const currentSequence = sequenceControl.value;

    if (currentSequence?.list) {
      const newList = [...currentSequence.list];
      newList[blockIndex] = blockList;

      sequenceControl.setValue({ list: newList }, { emitEvent: false });
    }

    const existingIds = new Set(this.thisExams.map((e) => e.id));
    const parsedNormalizedSelection: SequenceBlock[] = [];

    normalizedSelection.forEach((exam) => {
      if (!existingIds.has(exam.id)) {
        this.thisExams.push(this.parseEntityToBlock(exam));
        this.allSelectedExams = [...this.allSelectedExams, exam];
      }
      parsedNormalizedSelection.push(this.parseEntityToBlock(exam));
    });

    this.dataSequceneList[blockIndex] = [...parsedNormalizedSelection];
    this.rebuildExamCache();
    this.cdr.markForCheck();
  }

  async receiveSearchQuery(query: string, blockIndex: number) {
    this.examsSearchQuery = query?.trim() || null;

    if (this.examsSearchQuery && this.examsSearchQuery.length >= 3) {
      const searchParams = { 'filter[idOrNameLike]': this.examsSearchQuery };
      await this.examService.ensureLoaded(searchParams);

      this.searchResultObservables[blockIndex] = this.store.select(
        this.examService.allByQuery(searchParams),
      );
    }
    else {
      // default paginated mode
      const sequencePageQuery = {
        'page[size]': this.pageSize,
        'page[number]': this.currentPage,
        'sort': 'name',
      };
      await this.examService.ensureLoaded(sequencePageQuery);
      this.searchResultObservables[blockIndex] = this.store
        .select(this.examService.allByQuery(sequencePageQuery))
        .pipe(
          map((paginated) => {
            return [...this.allSelectedExams, ...paginated].filter(
              (seq, index, self) =>
                index === self.findIndex((s) => s.id === seq.id),
            );
          }),
        );
    }
  }

  async loadMoreExams(
    event: { pageNumber: number, pageSize: number },
    blockIndex: number,
  ) {
    if (!this.examsSearchQuery) {
      const examsPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      // Load next page from API
      await this.examService.ensureLoaded(examsPageQuery);

      // Get current sequences (previously loaded + selected)
      const currentExams = await firstValueFrom(
        this.searchResultObservables[blockIndex] ?? of([]),
      );

      // Combine previously loaded + new ones from store
      this.searchResultObservables[blockIndex] = this.store
        .select(this.examService.allByQuery(examsPageQuery))
        .pipe(
          map((paginated) => {
            return [
              ...this.allSelectedExams,
              ...currentExams,
              ...paginated,
            ].filter(
              (seq, index, self) =>
                index === self.findIndex((s) => s.id === seq.id),
            );
          }),
        );
    }
  }

  reorderExams(event: CdkDragDrop<SequenceBlock[]>, blockIndex: number) {
    const control = this.formGroup.get('sequence');
    if (!control) {
      return;
    }

    const sequenceValue = control.value as SequenceList;
    const newList = [...sequenceValue.list];
    const targetBlock = [...(newList[blockIndex] || [])];
    moveItemInArray(targetBlock, event.previousIndex, event.currentIndex);
    newList[blockIndex] = targetBlock;

    control.setValue({ list: newList });
  }

  reorderBlocks(event: CdkDragDrop<SequenceBlock[][]>) {
    const control = this.formGroup.get('sequence');
    if (!control) {
      return;
    }

    const sequenceValue = control.value as SequenceList;
    const newList = [...sequenceValue.list];
    moveItemInArray(newList, event.previousIndex, event.currentIndex);

    control.setValue({ list: newList });
  }

  getExamName(examId: string) {
    return this.examCache.get(examId)?.name;
  }

  getExamApproval(examId: string): Approval {
    return this.examCache.get(examId)?.approval as Approval;
  }

  getBlockName(blockIndex: number): string {
    const sequenceValue = this.formGroup.get('sequence')?.value as
      | SequenceList |
      undefined;
    const currentList = sequenceValue?.list || [];
    if (currentList[blockIndex]?.length <= 1) {
      return 'Aufgabe';
    }
    return 'zusammenhängende Aufgaben';
  }

  statusChanged(newStatus: string) {
    this.formGroup.controls.approval?.setValue(newStatus);
  }

  public getStateColor(examId: string): string {
    return getStatusColor(this.getExamApproval(examId));
  }

  public getStateText(examId: string): string {
    return getStatusText(this.getExamApproval(examId));
  }

  public addExamBlock() {
    // add new values to both the form group and the map which handles the observables for the dropdown field
    this.searchResultObservables[
      Object.keys(this.searchResultObservables).length
    ] = new Observable<ExamEntity[]>();
    const sequenceControl = this.formGroup.get('sequence');
    const sequenceValue = sequenceControl?.value as SequenceList | undefined;
    const currentList = sequenceValue?.list || [];
    const newList = [...currentList, []];
    sequenceControl?.patchValue({ list: newList });
    this.cdr.markForCheck();
  }

  public getlabelName(): string {
    return 'Testaufgaben';
  }

  public getPlaceholder(): string {
    return 'Suchbegriff eingeben um Ergebnisse zu erhalten';
  }

  public onSave(): void {
    if (this.formGroup.invalid) return;

    const formValue = this.formGroup.value;
    const sequenceList = (formValue.sequence?.list as SequenceBlock[][]) || [];

    // clean out empty sequence blocks before saving
    const cleanedList = sequenceList.filter(
      (block) => Array.isArray(block) && block.length > 0,
    );

    const cleanedSequence: SequenceList = {
      list: cleanedList.length > 0 ? cleanedList : [[]],
    };

    const updatedSequence = Object.assign(
      new SequenceEntity(),
      { id: this.data?.id },
      formValue,
      {
        sequence: cleanedSequence,
        consumers: formValue.consumers ? { id: formValue.consumers } : [],
        subjects: formValue.subjects ? { id: formValue.subjects } : [],
        exams: (formValue.exams as string[])?.length ?
          (formValue.exams as string[]).map((id) => ({ id })) :
          [],
        exercises: (formValue.exercises as string[])?.length ?
          (formValue.exercises as string[]).map((id) => ({ id })) :
          [],
      },
    );

    this.dialogRef.close(updatedSequence);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
